/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  CELL_TEXT_RIGHT_CLASS,
  code3Formatter,
  conditionalRowPostCreateDecorator,
  createDefaultGridModel,
  defaultCurrencyValueFormatterDecorator,
  ROW_FOOTER_LIGHT_CLASS,
  textOverlayRowPostCreate,
} from '@gh/common-internal-shared';
import { getRouteInitData, NumberTypes } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildEditableNumberColumn,
  buildNumberColumn,
  editableTypedValueFormatter,
  FocusedCell,
  GridDefinition,
  GridDefinitionFactory,
  mergePinnedRowValueFormatter,
  typedValueFormatter,
} from '@gh/core-ui-grid';
import { constant } from 'lodash';
import { Observable } from 'rxjs';
import {
  calculateDrawLendersTotal,
  DrawDetailsLendersScheduleItem,
  getDrawLendersScheduleItemId,
  getLendersScheduleItemFieldState,
  isDrawLendersScheduleItemOrderCostDrawn,
  isDrawLendersScheduleItemTotal,
  validateLendersScheduleItemField,
} from '../../../resources/draw-details.model';

@Component({
  standalone: false,
  selector: 'gh-draw-details-included-list',
  templateUrl: 'draw-details-lenders-included-list.component.html',
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrawDetailsLendersIncludedListComponent implements OnInit {
  @Input() editable$: Observable<boolean>;
  @Input() focusedCell?: FocusedCell;
  @Input() progressDrawn: number;

  @Output() focusedCellChange = new EventEmitter<FocusedCell>();
  @Output() setThisDraw = new EventEmitter<{ item: DrawDetailsLendersScheduleItem; thisDraw?: number }>();

  gridDefinition: GridDefinition;

  scheduleTotal: DrawDetailsLendersScheduleItem[];
  totalDrawnWillBe: number;
  private _schedule: DrawDetailsLendersScheduleItem[];

  @Input()
  set schedule(schedule: DrawDetailsLendersScheduleItem[]) {
    const total = calculateDrawLendersTotal(schedule);

    this._schedule = schedule;
    this.scheduleTotal = [total];
    this.totalDrawnWillBe = (total.thisDraw || 0) + this.progressDrawn;
  }

  get schedule(): DrawDetailsLendersScheduleItem[] {
    return this._schedule;
  }

  constructor(private route: ActivatedRoute,
              private gridDefinitionFactory: GridDefinitionFactory,
              private translations: TranslationService) {

  }

  ngOnInit(): void {
    const { includedLendersCostCentersGridLayout } = getRouteInitData(this.route);

    const model = createDefaultGridModel({
      editable: this.editable$,
      getId: getDrawLendersScheduleItemId,
      getCellDef: getLendersScheduleItemFieldState,
      validate: validateLendersScheduleItemField,
      command: (entity, field, value) => this.setThisDraw.emit({ item: entity, thisDraw: value }),
    });

    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBasedWithModel({
      model,
      layout: includedLendersCostCentersGridLayout,
      columns: {
        code: mergePinnedRowValueFormatter({
          valueFormatter: code3Formatter,
        }, constant('')),
        orderNumber: {
          cellClass: CELL_TEXT_RIGHT_CLASS,
        },
        orderCost: mergePinnedRowValueFormatter({
          ...buildNumberColumn(NumberTypes.Price),
        }, defaultCurrencyValueFormatterDecorator(typedValueFormatter(NumberTypes.Price))),
        orderCostPct: mergePinnedRowValueFormatter({
          ...buildNumberColumn(NumberTypes.Percents_2),
        }, typedValueFormatter(NumberTypes.Percents_2)),
        contractCostPct: mergePinnedRowValueFormatter({
          ...buildNumberColumn(NumberTypes.Percents_2),
        }, typedValueFormatter(NumberTypes.Percents_2)),
        amountDrawn: mergePinnedRowValueFormatter({
          ...buildNumberColumn(NumberTypes.Price),
          cellClassRules: {
            'text-bold': ({ data }: { data: DrawDetailsLendersScheduleItem }) => data.amountDrawn > data.orderCost,
          },
        }, defaultCurrencyValueFormatterDecorator(typedValueFormatter(NumberTypes.Price))),
        thisDraw: mergePinnedRowValueFormatter({
          cellClassRules: {
            'editable-cell--green': ({ data }: { data: DrawDetailsLendersScheduleItem }) =>
              isDrawLendersScheduleItemOrderCostDrawn(data) || data.orderCost < 0,
            'text-bold': ({ data }: { data: DrawDetailsLendersScheduleItem }) =>
              !!(data.thisDraw && data.thisDraw > data.orderCost),
          },
          ...buildEditableNumberColumn(NumberTypes.Price, model.getCellAccessor('thisDraw')),
        }, defaultCurrencyValueFormatterDecorator(editableTypedValueFormatter(NumberTypes.Price))),
      },
      options: {
        defaultColDef: {
          sortable: false,
        },
        rowClassRules: {
          [ROW_FOOTER_LIGHT_CLASS]: ({ data }: { data: any }) => isDrawLendersScheduleItemTotal(data),
        },
        processRowPostCreate: conditionalRowPostCreateDecorator(
          ({ node: { data } }) => isDrawLendersScheduleItemTotal(data),
          textOverlayRowPostCreate(`${this.translations.get('lbl_total')}:`)),
      },
    });
  }

  onCopyOrderCost(item: DrawDetailsLendersScheduleItem): void {
    this.setThisDraw.emit({ item, thisDraw: item.balance });
  }

}
